import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Inventory2 as Inventory2Icon,
  AttachMoney as AttachMoneyIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { PublicCatalogLayout } from '../../components/layout/PublicCatalogLayout';
import { productCatalogApi } from '../../api/productCatalog';

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isNaN(n)) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return String(value);
}

function productMatchesQuery(p, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    p.title,
    p.description,
    p.id,
    p.currency,
    p.price != null ? String(p.price) : '',
    p.quantity != null ? String(p.quantity) : '',
    p.branch,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export const ProductCatalogPublic = () => {
  const { companyId, catalogSlug } = useParams();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicCatalog', companyId, catalogSlug],
    queryFn: () => productCatalogApi.getPublicCatalog(companyId, catalogSlug),
    enabled: !!companyId && !!catalogSlug,
  });

  const products = data?.products || [];
  const filteredProducts = useMemo(
    () => products.filter((p) => productMatchesQuery(p, search)),
    [products, search],
  );

  if (isLoading) {
    return (
      <PublicCatalogLayout>
        <Paper
          elevation={0}
          sx={{
            py: 10,
            px: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Cargando catálogo…
          </Typography>
        </Paper>
      </PublicCatalogLayout>
    );
  }

  if (error) {
    return (
      <PublicCatalogLayout>
        <Alert
          severity="error"
          sx={{
            borderRadius: 2,
            maxWidth: 560,
            boxShadow: (theme) => theme.shadows[1],
          }}
        >
          {error.message || 'No se pudo cargar el catálogo'}
        </Alert>
      </PublicCatalogLayout>
    );
  }

  const totalCount = products.length;
  const showingCount = filteredProducts.length;

  return (
    <PublicCatalogLayout>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
              theme.palette.primary.main,
              0,
            )} 48%)`,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.5,
                lineHeight: 1.2,
                color: 'text.primary',
              }}
            >
              {data?.title || 'Catálogo'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 520 }}>
              Explore los productos disponibles. Use el buscador para filtrar por nombre, precio o
              sucursal.
            </Typography>
          </Box>
          <Chip
            label={
              search.trim()
                ? `${showingCount} de ${totalCount} productos`
                : `${totalCount} ${totalCount === 1 ? 'producto' : 'productos'}`
            }
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
          />
        </Stack>
      </Paper>

      <TextField
        fullWidth
        placeholder="Buscar productos…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Buscar en el catálogo"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', opacity: 0.85 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          maxWidth: 520,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'background.paper',
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: (theme) => theme.shadows[1],
            },
            '&.Mui-focused': {
              boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          },
        }}
        size="small"
      />

      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        {filteredProducts.map((p, idx) => {
          const key = p.id != null ? String(p.id) : `item-${idx}`;
          const priceStr = formatPrice(p.price);
          const currency = p.currency ? String(p.currency) : '';
          return (
            <Grid item key={key} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.shadows[1],
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[6],
                    transform: 'translateY(-3px)',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    p: 2,
                  }}
                >
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      display="block"
                      sx={{ letterSpacing: 1, fontWeight: 700, fontSize: '0.65rem', lineHeight: 1.5 }}
                    >
                      Nombre
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      component="h2"
                      sx={{
                        fontWeight: 800,
                        mt: 0.25,
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {p.title || '—'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                      overflow: 'hidden',
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 2,
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: (theme) =>
                          `linear-gradient(110deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                            theme.palette.primary.main,
                            0.03,
                          )} 55%, transparent 100%)`,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      {p.imageUrl ? (
                        <Box
                          component="img"
                          src={p.imageUrl}
                          alt={p.title || 'Producto'}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                          Sin imagen
                        </Typography>
                      )}
                    </Box>

                    {priceStr ? (
                      <Box
                        sx={{
                          p: 2,
                          borderBottom: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
                              color: 'warning.dark',
                            }}
                          >
                            <AttachMoneyIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontWeight: 700,
                                letterSpacing: 0.3,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                              }}
                            >
                              Precio
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                mt: 0.35,
                                fontWeight: 800,
                                lineHeight: 1.3,
                                color: 'text.primary',
                                wordBreak: 'break-word',
                              }}
                            >
                              {currency ? `${currency} ` : ''}
                              {priceStr}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.25, display: 'block', fontSize: '0.65rem' }}
                            >
                              Precios sujetos a cambios
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}

                    <Box>
                      <Box
                        
                        sx={{
                          p: 2,
                          borderRight: { xs: 0, sm: 1 },
                          borderBottom: { xs: 1, sm: 0 },
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                              color: 'info.main',
                            }}
                          >
                            <InventoryIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}
                            >
                              Cantidad
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ mt: 0.35, fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}
                            >
                              {p.quantity != null && p.quantity !== '' ? String(p.quantity) : '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
                              color: 'success.dark',
                            }}
                          >
                            <StoreIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}
                            >
                              Sucursal
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.35,
                                fontWeight: 600,
                                lineHeight: 1.4,
                                color: 'text.primary',
                                wordBreak: 'break-word',
                              }}
                            >
                              {p.branch != null && p.branch !== '' ? String(p.branch) : '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {products.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            py: 6,
            px: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Inventory2Icon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5, opacity: 0.8 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Catálogo vacío
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
            No hay productos para mostrar en este momento.
          </Typography>
        </Paper>
      ) : filteredProducts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            py: 5,
            px: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <SearchIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5, opacity: 0.75 }} />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Sin resultados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            No hay coincidencias para &quot;{search.trim()}&quot;. Pruebe con otras palabras o revise la
            ortografía.
          </Typography>
        </Paper>
      ) : null}
    </PublicCatalogLayout>
  );
};
